import { useParams } from 'react-router';

import { useMemo } from 'react';

import { useTokenClaims } from '@app-velocity/request-gateway';

import { TCurrentUserTeamDto } from '@app-velocity/av-api-v2';

import {
  useGetTeamsQuery,
  useProjectsQuery,
} from '@app-velocity/libs/teams-data-access';

import { useCurrentTeam } from '../useCurrentTeam';

export const useWorkspaceSwitcherData = () => {
  const readTokenClaim = useTokenClaims();
  const userId = readTokenClaim('sub')?.toString();
  const { data: teams, isLoading: areTeamsLoading } = useGetTeamsQuery();
  const { data: projects, isLoading: areWorkspacesLoading } =
    useProjectsQuery(userId);
  const { workspaceId, teamId } = useParams<{
    workspaceId: string;
    teamId: string;
  }>();
  const { activeWorkspace, activeTeam } = useCurrentTeam(
    userId,
    workspaceId,
    teamId
  );

  const teamsWithWorkspaces = useMemo(() => {
    if (!teams || !projects) return [];

    return teams.map((team) => {
      const workspaceWithTeam = projects.find(
        (project) => project.teamId === team.teamId
      );

      return {
        ...team,
        workspaceId: workspaceWithTeam?.id || '',
      };
    });
  }, [teams, projects]);

  const teamsByOrganization = useMemo(() => {
    const teams: {
      noOrganization: (TCurrentUserTeamDto & { workspaceId: string })[];
      byOrganization: {
        [organizationId: string]: {
          organizationName: string;
          teams: (TCurrentUserTeamDto & { workspaceId: string })[];
        };
      };
    } = {
      noOrganization: [],
      byOrganization: {},
    };
    teamsWithWorkspaces.forEach((team) => {
      if (!team.organizationId) {
        teams.noOrganization.push(team);
      } else {
        if (!teams.byOrganization[team.organizationId]) {
          teams.byOrganization[team.organizationId] = {
            organizationName: team.organizationName || '-',
            teams: [],
          };
        }

        teams.byOrganization[team.organizationId].teams.push(team);
      }
    });

    return teams;
  }, [teamsWithWorkspaces]);

  const activeTeamWithWorkspace = {
    ...activeTeam,
    workspaceId: activeWorkspace?.id || '',
    name: activeWorkspace?.name || '',
  };

  return {
    teamsWithWorkspaces,
    teams: teamsByOrganization,
    activeTeam: activeTeamWithWorkspace,
    isLoading: areTeamsLoading || areWorkspacesLoading,
  };
};
